import { EarningsItem, MovePeriod } from '@api/api/earnings';
import { useThemeEarningsData } from '@terminal/views/theme-overview/tab-content/theme-earnings/useThemeEarningsData';
import { useThemeEarningsDrivers } from '@terminal/views/theme-overview/tab-content/theme-earnings/useThemeEarningsDrivers';
import {
  getMedian,
  getSurpriseStat,
} from '@terminal/views/theme-overview/tab-content/theme-earnings/utils';
import { handlePriceData } from '@terminal/widgets/earnings/utils/tableUtils';
import { useMemo } from 'react';

export type IndustryThemeTimeframe = {
  startDate: Date;
  endDate: Date;
};

const getPeersMedian = (earnings: EarningsItem[], period: MovePeriod) =>
  getMedian(
    earnings
      .map(earning => handlePriceData(earning.price, period))
      .filter((change): change is number => change !== undefined)
  );

const getSurpriseCounts = (
  earnings: EarningsItem[],
  metric: 'eps' | 'revenue'
) => {
  const surprises = earnings
    .map(earning => earning[metric].surprise)
    .filter((surprise): surprise is number => surprise !== undefined);

  return {
    beatCount: surprises.filter(surprise => surprise >= 0).length,
    surpriseCount: surprises.length,
  };
};

export const useIndustryThemeMetrics = (
  themeId: string,
  timeframe: IndustryThemeTimeframe
) => {
  const { earningsData, isLoading, isError, error } = useThemeEarningsData(
    themeId,
    {
      startDate: timeframe.startDate,
      endDate: timeframe.endDate,
    }
  );

  const reportedEarnings = useMemo(
    () => earningsData.filter(earning => earning.reported),
    [earningsData]
  );

  const { drivers, isFetching: isDriversFetching } =
    useThemeEarningsDrivers(reportedEarnings);

  const epsCounts = getSurpriseCounts(reportedEarnings, 'eps');
  const revenueCounts = getSurpriseCounts(reportedEarnings, 'revenue');

  return {
    earningsData,
    reportedCount: reportedEarnings.length,
    totalCount: earningsData.length,
    epsStat: getSurpriseStat(reportedEarnings, 'eps'),
    epsBeatCount: epsCounts.beatCount,
    epsSurpriseCount: epsCounts.surpriseCount,
    revenueStat: getSurpriseStat(reportedEarnings, 'revenue'),
    revenueBeatCount: revenueCounts.beatCount,
    revenueSurpriseCount: revenueCounts.surpriseCount,
    medianT1: getPeersMedian(reportedEarnings, 'd1'),
    medianT2: getPeersMedian(reportedEarnings, 'd2'),
    leadingDriver: drivers[0],
    isLoading: isLoading || isDriversFetching,
    isError,
    error,
  };
};
